/**
 * Error suppression utility for development environment
 * Suppresses known non-critical errors from browser extensions and dev tools
 */

function isMobxStateTreeError(message: string): boolean {
	const lowerMessage = message.toLowerCase();
	return (
		// Direct mobx-state-tree references
		lowerMessage.includes("[mobx-state-tree]") ||
		lowerMessage.includes("mobx-state-tree") ||
		lowerMessage.includes("mst") ||
		// Common mobx-state-tree error patterns
		lowerMessage.includes("you are trying to read or write") ||
		lowerMessage.includes("no longer part of a state tree") ||
		lowerMessage.includes("undoablecontentmodel") ||
		lowerMessage.includes("designerstate") ||
		lowerMessage.includes("contentmodel") ||
		// Mobx general errors
		(lowerMessage.includes("error") && lowerMessage.includes("mobx")) ||
		(lowerMessage.includes("warning") && lowerMessage.includes("mobx")) ||
		// Builder.io related mobx errors
		(lowerMessage.includes("builder.io") && lowerMessage.includes("mobx")) ||
		// Common mobx state tree stack traces
		lowerMessage.includes("at mobx") ||
		lowerMessage.includes("at mst") ||
		// Mobx observer patterns
		(lowerMessage.includes("observer") && lowerMessage.includes("mobx")) ||
		// State tree specific errors
		lowerMessage.includes("state tree") ||
		lowerMessage.includes("statetree") ||
		// Additional patterns
		(lowerMessage.includes("cannot read properties") &&
			lowerMessage.includes("mobx")) ||
		(lowerMessage.includes("cannot set property") &&
			lowerMessage.includes("mobx"))
	);
}

// Suppress MobX State Tree errors from browser extensions/dev tools
const originalConsoleError = console.error;
console.error = function (...args: any[]) {
	// Check if this is a MobX State Tree error from browser extensions or Builder.io
	const message = String(args[0] || "");

	if (isMobxStateTreeError(message)) {
		// Skip logging this error as it's from browser extensions or Builder.io editor, not our app
		return;
	}

	// Skip profile-related errors that are now handled gracefully
	if (message.includes("Could not find profile for mentee")) {
		return;
	}

	// Log all other errors normally
	originalConsoleError.apply(console, args);
};

// Also suppress warnings for the same issue
const originalConsoleWarn = console.warn;
console.warn = function (...args: any[]) {
	const message = String(args[0] || "");

	if (isMobxStateTreeError(message)) {
		return;
	}

	originalConsoleWarn.apply(console, args);
};

// Also suppress console.info and console.log for mobx-state-tree
const originalConsoleInfo = console.info;
console.info = function (...args: any[]) {
	const message = String(args[0] || "");

	if (isMobxStateTreeError(message)) {
		return;
	}

	originalConsoleInfo.apply(console, args);
};

const originalConsoleLog = console.log;
console.log = function (...args: any[]) {
	const message = String(args[0] || "");

	if (isMobxStateTreeError(message)) {
		return;
	}

	originalConsoleLog.apply(console, args);
};

// Handle unhandled promise rejections
window.addEventListener(
	"unhandledrejection",
	(event: PromiseRejectionEvent) => {
		const message = String((event as PromiseRejectionEvent).reason ?? "");
		if (isMobxStateTreeError(message)) {
			event.preventDefault();
			return;
		}
	},
);

// Handle global errors
window.addEventListener("error", (event) => {
	const message = String(event.error?.message || event.message || "");
	if (isMobxStateTreeError(message)) {
		event.preventDefault();
		return;
	}
});

// Override window.onerror for additional coverage
const originalOnError = window.onerror;
window.onerror = function (message, source, lineno, colno, error) {
	const errorMessage = String(message || error?.message || "");
	if (isMobxStateTreeError(errorMessage)) {
		return true; // Prevent default error handling
	}

	if (originalOnError) {
		return originalOnError.call(this, message, source, lineno, colno, error);
	}
	return false;
};

export {};
